import type { CityConfig } from "../hawaii-city/types";

export const mauiConfig: CityConfig = {
  city: "Maui",
  state: "Hawaii",
  stateAbbr: "HI",
  slug: "maui",
  route: "/cities/maui",
  title: "Emergency & Expedited Passport Services in Maui, HI — Island-Wide Processing | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services across Maui, HI. Fast passport renewal and urgent passport help for all Maui island residents and visitors. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Maui, HI",
  heroSubtitle: "All of Maui — from Kahului to Lahaina, Kihei to Hana — get your passport fast without leaving the island. Start online, ship overnight, and we handle the rest. As fast as 24 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Breathtaking%20Maui%20Hawaii%20landscape%20with%20Haleakala%20volcano%20in%20background%2C%20lush%20green%20tropical%20valleys%2C%20Road%20to%20Hana%20coastline%20with%20waterfalls%2C%20warm%20golden%20sunset%20light%2C%20palm%20trees%20and%20tropical%20vegetation%2C%20professional%20travel%20photography%2C%20vibrant%20warm%20colors%20with%20deep%20blue%20ocean%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=maui-hero-01&orientation=landscape",
  heroImageAlt: "Maui Hawaii landscape with Haleakala volcano and tropical coastline",
  geoPosition: "20.7984;-156.3319",
  geoPlacename: "Maui, Hawaii",
  regionalAgency: {
    name: "Honolulu Passport Agency",
    address: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
    distance: "Inter-island flight to Honolulu — about 100 miles",
  },
  nearbyCitiesZips: [
    { name: "Kahului", zips: "96732" },
    { name: "Wailuku", zips: "96793" },
    { name: "Kihei", zips: "96753" },
    { name: "Lahaina", zips: "96761" },
    { name: "Wailea", zips: "96753" },
    { name: "Paia", zips: "96779" },
    { name: "Makawao", zips: "96768" },
    { name: "Pukalani", zips: "96768" },
    { name: "Kula", zips: "96790" },
    { name: "Kaanapali", zips: "96761" },
    { name: "Napili", zips: "96761" },
    { name: "Honokowai", zips: "96761" },
    { name: "Hana", zips: "96713" },
    { name: "Honolulu", zips: "96815" },
    { name: "Kailua", zips: "96734" },
    { name: "Hilo", zips: "96720" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from anywhere on Maui in minutes." },
    { icon: "ri-truck-line", title: "Ship Your Documents", desc: "Use overnight shipping from any Maui town to send your documents directly to us." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission to reduce errors and delays." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots — no public appointment needed for Maui residents." },
  ],
  testimonials: [
    {
      name: "Rachel M.",
      location: "Makawao, HI",
      rating: 5,
      text: "I had a business trip to London in 5 days and my passport was expired. Fast Passport Center processed my renewal from Upcountry Maui in 4 days. The shipping was seamless and their team kept me updated.",
      service: "Passport Renewal",
    },
    {
      name: "James L.",
      location: "Paia, HI",
      rating: 5,
      text: "Our family trip to Bali was in 2 weeks and my daughter's passport was nowhere to be found. They walked us through the replacement process from Paia and had a new passport with time to spare.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Sophia H.",
      location: "Kaanapali, HI",
      rating: 5,
      text: "Emergency trip to Canada for a family matter. I'm in Kaanapali on West Maui and FPC handled everything remotely. The passport arrived in 5 days. No need to fly to Honolulu for an agency appointment.",
      service: "Expedited Passport",
    },
  ],
  facilities: [
    {
      id: "kahului-usps-maui",
      name: "Kahului Post Office",
      type: "USPS Passport Acceptance",
      address: "138 Puunene Ave",
      city: "Kahului",
      state: "HI",
      zip: "96732",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Central Maui — Kahului",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Appointment required. Call ahead to confirm passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "wailuku-usps-maui",
      name: "Wailuku Post Office",
      type: "USPS Passport Acceptance",
      address: "250 Imi Kala St",
      city: "Wailuku",
      state: "HI",
      zip: "96793",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Central Maui — about 3 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Wailuku area option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "kihei-usps-maui",
      name: "Kihei Post Office",
      type: "USPS Passport Acceptance",
      address: "1250 S Kihei Rd",
      city: "Kihei",
      state: "HI",
      zip: "96753",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "South Maui — about 15 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "South Maui option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "lahaina-usps-maui",
      name: "Lahaina Post Office",
      type: "USPS Passport Acceptance",
      address: "1760 Honoapiilani Hwy",
      city: "Lahaina",
      state: "HI",
      zip: "96761",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "West Maui — about 25 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "West Maui option. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport on Maui, HI?",
      a: "Through Fast Passport Center, expedited processing can be as fast as 24 hours. Standard processing through a Maui post office can take 2–6 months. For Maui residents, our courier service is the fastest option without flying to Honolulu.",
    },
    {
      q: "Do I need to fly to Honolulu for a passport?",
      a: "No. Most Maui travelers start online and ship documents via overnight courier. The Kahului, Wailuku, Kihei, or Lahaina Post Offices can serve as acceptance facilities if needed, but you don't need to visit the Honolulu agency.",
    },
    {
      q: "Where is the closest passport acceptance facility on Maui?",
      a: "The Kahului Post Office at 138 Puunene Ave and the Wailuku Post Office at 250 Imi Kala St both accept passport applications by appointment. Kihei and Lahaina Post Offices are also options for South and West Maui residents.",
    },
    {
      q: "What if I'm a tourist on Maui and need a passport emergency?",
      a: "We frequently help tourists and vacationers across Maui who discover passport issues while traveling. We can ship to your resort or hotel address in Kihei, Wailea, Kaanapali, or Lahaina and coordinate return shipping so you don't miss your flight.",
    },
    {
      q: "Can I get an expedited passport if I've never had one before?",
      a: "Yes, but first-time applicants require an in-person acceptance clerk visit. The Kahului or Wailuku Post Office are the most convenient options for Maui residents. We guide you through exactly what to bring.",
    },
    {
      q: "How does shipping from Maui work?",
      a: "You can ship overnight using FedEx or UPS from any town on Maui — Kahului, Kihei, Lahaina, or Wailuku. Most Maui residents use the FedEx location near Kahului Airport or the UPS Store in Wailuku.",
    },
    {
      q: "Is Fast Passport Center an official government agency?",
      a: "No — Fast Passport Center is a private, approved and registered U.S. Department of State passport courier service. We work within the official passport system to provide expedited processing.",
    },
    {
      q: "What if I need a child passport on Maui?",
      a: "Child passports (for minors 15 and under) require parental participation and an in-person acceptance step — both parents typically must consent. We provide clear instructions for Maui families.",
    },
    {
      q: "Can I get a passport if mine is lost or stolen?",
      a: "Yes. Lost or stolen passport replacements often require additional forms (including Form DS-64). We guide Maui residents through the exact process, including the acceptance clerk visit and expedited submission.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Kahului", address: "45 Kamehameha Ave, Kahului, HI 96732", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Kihei", address: "1215 S Kihei Rd, Kihei, HI 96753", hours: "Daily 7 AM – 11 PM" },
    { name: "CVS Pharmacy — Kahului", address: "15 Hoalike Ave, Kahului, HI 96732", hours: "Daily 7 AM – 10 PM" },
    { name: "The UPS Store — Kahului", address: "270 Dairy Rd, Kahului, HI 96732", hours: "Call for hours" },
    { name: "Walmart — Kahului", address: "101 Pakaula St, Kahului, HI 96732", hours: "Call for hours" },
  ],
  dmvLocations: [
    {
      name: "Kahului DMV — Maui County Service Center",
      address: "70 E Kaahumanu Ave, Kahului, HI 96732",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "Driver licenses, REAL ID, state IDs. Appointment recommended.",
      featured: true,
    },
    {
      name: "Kihei DMV — Kihei Satellite City Hall",
      address: "1215 S Kihei Rd, Kihei, HI 96753",
      hours: "Mon–Fri, 7:45 AM – 4:00 PM",
      note: "South Maui option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "OGG",
      name: "Kahului Airport",
      distance: "Primary airport — Maui",
      address: "1 Keolani Pl, Kahului, HI 96732",
      note: "Major airport for Maui with direct flights to mainland U.S., Canada, and inter-island connections. Most Maui travelers fly from OGG for international trips.",
      flightUrl: "https://www.hawaii.gov/ogg/",
      flightLabel: "OGG Airport Website",
      featured: true,
    },
    {
      code: "HNL",
      name: "Daniel K. Inouye International Airport",
      distance: "Inter-island flight — Honolulu",
      address: "300 Rodgers Blvd, Honolulu, HI 96819",
      note: "Major international hub. Many Maui travelers connect through HNL for international destinations.",
      flightUrl: "https://www.hawaii.gov/hnl/",
      flightLabel: "HNL Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from anywhere on Maui.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Maui travelers renew fast and avoid delays — no agency visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants typically require an in-person acceptance clerk visit. We guide Maui residents through every step.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements often require additional forms. We guide you through exactly what's needed to avoid costly mistakes.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors (15 and under) require parental participation and an in-person acceptance step. We provide clear instructions for Maui families.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports for Maui residents.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport & Damaged",
      description: "Need a second passport for frequent travel, or replacing a damaged one? We handle both efficiently.",
      bullets: ["Frequent traveler solutions", "Damaged passport replacement", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Maui travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for Maui residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance for Maui residents with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for Maui residents with lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16 on Maui, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service for Maui residents after marriage, divorce, or legal name changes." },
    { name: "Second Passport & Damaged", description: "Second passport issuance for frequent Maui travelers and damaged passport replacement." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Government processing times change constantly. Many Maui travelers assume 6–8 weeks is safe, only to find out it's now 3–4 months. Apply as soon as you book travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs so you get it right the first time." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms", description: "One missing signature or wrong date and your application gets sent back. Our checklist catches errors before submission, saving you weeks of delay." },
    { icon: "ri-pen-nib-line", title: "Wrong Proof of Citizenship", description: "Not all birth certificates are accepted. Some require a long-form version. We tell you exactly which documents you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Missing Travel Proof for Emergency", description: "Regional agencies require proof of urgent travel. Without it, you're turned away. We help you prepare the right documentation before you need it." },
    { icon: "ri-map-pin-time-line", title: "Not Checking Name Consistency", description: "Your passport name must match your driver's license and ticket exactly. Middle names, suffixes, and hyphenated names cause more rejections than you'd think." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option from any Maui town." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you don't have to fly to Honolulu." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up." },
  ],
  processingOptions: [
    { speed: "24", unit: "Hours*", label: "Regional Agency Processing", tag: "Emergency Only", tagColor: "bg-gray-100 text-gray-500", features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"], highlight: false, emergency: true },
    { speed: "2–9", unit: "Business Days", label: "Fast Passport Center", tag: "Most Popular", tagColor: "bg-[#F6C344] text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: true },
    { speed: "3–5", unit: "Weeks", label: "Fast Passport Center", tag: "Standard Expedited", tagColor: "bg-[#1A365D]/10 text-[#1A365D]", features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"], highlight: false, smartSaver: true },
    { speed: "1–2", unit: "Months", label: "Self-Service (Post Office)", tag: "Slowest", tagColor: "bg-gray-100 text-gray-400", features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"], highlight: false, muted: true },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited availability, often weeks out", fpc: "No appointment needed to start", fpcGood: true },
    { feature: "In-Person Visit", agency: "Required — fly to Honolulu", fpc: "Most travelers ship documents overnight from Maui", fpcGood: true },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options", fpcGood: true },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review to reduce delays", fpcGood: true },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability", fpcGood: true },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected", fpcGood: true },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documents", fpcGood: true },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Kahului", route: "/cities/kahului" },
    { name: "Kihei", route: "/cities/kihei" },
    { name: "Lahaina", route: "/cities/lahaina" },
    { name: "Honolulu", route: "/cities/honolulu" },
    { name: "Kailua", route: "/cities/kailua" },
    { name: "Hilo", route: "/cities/hilo" },
  ],
  fpcOfficeAddress: "300 Ala Moana Blvd, Room 1-224, Honolulu, HI 96850",
  fpcOfficeNote: "After signing up online, choose what works best for you: courier pickup from your Maui home, ship with our prepaid label from any UPS/FedEx location in Kahului, Kihei, or Lahaina, or schedule a drop-off. Most Maui clients ship overnight.",
  fpcOfficeDistance: "Serving all of Maui remotely",
  serviceAreaIntro: "Whether you are in Kahului, Kihei, Lahaina, Wailuku, or anywhere on the Valley Isle, Fast Passport Center makes expedited passport processing simple — no flying to Honolulu, no agency lines.",
  nearbyCitiesIntro: "Anywhere on Maui — start online, ship overnight from your town, and we handle the rest.",
  comparisonIntro: "",
  howItWorksIntro: "From start to finish, we make getting an expedited passport on Maui as simple as possible.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Maui travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from real travelers across Maui who trusted Fast Passport Center with their expedited passports.",
  faqIntro: "Everything Maui travelers ask us most often about expedited passport services.",
  resourcesIntro: "",
  localTravelThemes: [],
  landmarks: [],
  island: "Maui",
  islandNickname: "The Valley Isle",
  landmarkCards: [
    {
      name: "Road to Hana",
      description: "The legendary 52-mile drive from Kahului to Hana winds through 600 curves and 54 one-lane bridges, past waterfalls and bamboo forests. One of the most beautiful drives in the world — all on one small island.",
      imageUrl: "https://readdy.ai/api/search-image?query=Road%20to%20Hana%20Maui%20Hawaii%2C%20winding%20tropical%20road%20with%20bamboo%20forest%2C%20waterfalls%20visible%20through%20lush%20green%20vegetation%2C%20dramatic%20coastal%20cliffs%2C%20warm%20dappled%20light%2C%20professional%20travel%20photography%2C%20lush%20Paradise%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=maui-hana&orientation=landscape",
    },
    {
      name: "Haleakala Crater",
      description: "Rising 10,023 feet above sea level, Haleakala's massive crater is a lunar-like landscape above the clouds. Watching sunrise from the summit is a transcendent experience that draws visitors from every corner of the globe.",
      imageUrl: "https://readdy.ai/api/search-image?query=Haleakala%20crater%20summit%20Maui%20Hawaii%20above%20the%20clouds%2C%20volcanic%20alien%20landscape%20with%20red%20and%20purple%20rock%2C%20golden%20sunrise%20breaking%20over%20cloud%20layer%2C%20silhouette%20of%20visitors%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=maui-haleakala-full&orientation=landscape",
    },
    {
      name: "Lahaina & Kaanapali",
      description: "Historic Lahaina town and the pristine Kaanapali Beach form West Maui's cultural and resort heart. International travelers from Japan, Canada, and Australia are year-round guests here.",
      imageUrl: "https://readdy.ai/api/search-image?query=Kaanapali%20Beach%20West%20Maui%20Hawaii%20with%20resort%20hotels%2C%20golden%20sand%20beach%2C%20turquoise%20calm%20Pacific%20Ocean%2C%20palm%20trees%2C%20warm%20sunset%20lighting%2C%20luxury%20tropical%20destination%2C%20professional%20travel%20photography%2C%20no%20readable%20text%20or%20signs&width=600&height=400&seq=maui-kaanapali-full&orientation=landscape",
    },
  ],
  travelTips: [
    { icon: "ri-flight-takeoff-line", title: "OGG Connects Globally", description: "Kahului Airport (OGG) has direct flights to Japan, Canada, and 20+ mainland cities. No matter where on Maui you live, OGG is your gateway — validate your passport before every international booking." },
    { icon: "ri-hotel-line", title: "Island-Wide Resort Coverage", description: "From Wailea to Napili, Maui's luxury resorts welcome international guests. Visitors who discover passport problems on arrival can ship from any Maui town — we process urgently." },
    { icon: "ri-map-pin-line", title: "No Passport Agency on Maui", description: "Maui has no regional passport agency. The closest is in Honolulu, requiring an inter-island flight. Fast Passport Center means you never have to leave Maui for passport processing." },
    { icon: "ri-truck-line", title: "Ship from Any Maui Town", description: "Kahului, Kihei, Lahaina, and Wailuku all have FedEx and UPS locations. Overnight shipping from any Maui town reaches our processing center the following morning." },
    { icon: "ri-sun-line", title: "Year-Round Travel Demand", description: "Maui's perfect climate means no slow season. International visitors arrive every month of the year — and so does passport urgency. We're available year-round, including holidays." },
    { icon: "ri-global-line", title: "Top 3 Hawaii Destination", description: "Maui is consistently ranked among the world's top travel destinations. Its international profile means residents and visitors alike have strong global travel ties." },
  ],
  statePageRoute: "/states/hawaii",
  statePageName: "Hawaii",
  dmvWebsite: "https://www.hawaii.gov/dot/highways/mvm",
  dmvLabel: "Hawaii DMV — Official Website",
};